//create a component similar to the other filters,but for distance its a dropdown with options 1 mile, 5 miles, 10 miles, 20 miles, 50 miles, 100 miles and 200 miles. The default value is 10 miles. When the user selects a distance and clicks save, the filter should be applied to the search results.
/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { Fragment, useEffect, useRef, useState } from 'react';

import filterMenuStyle from '../FiltersMenu/styles';
import { LocationSearchDefaultIcon, PlainDownArrow } from '../Icons';
import useStyles from './styles';

const FilterByDistance = ({
  onSave,
  currentValue,
  filterShowHide,
  disabilityOption,
  onDistanceChange,
}) => {
  const styles = useStyles();
  const filterMenuStyles = filterMenuStyle();
  // If currentValue is a finite number use it; otherwise default to
  // Nationwide (200) when disability filter is active (Disabilityoption === 1),
  // otherwise default to 10 miles.
  const defaultDistanceValue = (() => {
    const cv = Number(currentValue);
    if (Number.isFinite(cv)) return cv;
    return disabilityOption === 1 ? 200 : 10;
  })();

  const [selectedDistanceValue, setSelectedDistanceValue] =
    useState(defaultDistanceValue);

  // Options expressed in miles. 200 represents 'Nationwide' (no limit).
  const options = [5, 10, 15, 20, 25, 30, 40, 200];

  const wrapperRef = useRef(null);
  const listItemRefs = useRef([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [userSelected, setUserSelected] = useState(false);
  const [isKeyboard, setIsKeyboard] = useState(false);

  const handleDistanceChange = newValue => {
    setSelectedDistanceValue(newValue);
    setUserSelected(true);
    onDistanceChange?.();
    if (onSave) onSave({ Distance: newValue });
    setOpen(false);
  };
  // Keep component in sync when parent `currentValue` or `disabilityOption` changes
  useEffect(() => {
    const cv = Number(currentValue);
    if (Number.isFinite(cv)) {
      setSelectedDistanceValue(cv);
      setUserSelected(true);
    } else {
      const def = disabilityOption === 1 ? 200 : 10;
      setSelectedDistanceValue(def);
      setUserSelected(false);
    }
  }, [currentValue, disabilityOption]);

  // Scroll & Focus handling on Arrow Key adjustments
  useEffect(() => {
    if (open && activeIndex >= 0 && listItemRefs.current[activeIndex]) {
      const activeElement = listItemRefs.current[activeIndex];

      // Scroll the element into view cleanly inside its container
      activeElement.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [activeIndex, open]);

  const openAndFocus = () => {
    const selIndex = options.indexOf(selectedDistanceValue);
    const start = selIndex >= 0 ? selIndex : 0;
    setOpen(true);
    setActiveIndex(start);
  };

  // Open the list but don't focus any item. user must Tab to move focus into the list.
  const openButDontFocus = () => {
    setOpen(true);
    setActiveIndex(-1);
  };

  useEffect(() => {
    // Indicating keyboard interaction.
    const handleKeyDown = e => {
      if (e.key === 'Tab') {
        setIsKeyboard(true);
      }
    };

    // when user used a pointer (mouse/touch)
    const handlePointerDown = () => {
      setIsKeyboard(false);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, []);

  const handleKeyNavigation = (e, { origin = 'button' } = {}) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        // If invoked from the button and list is closed, open+focus start
        if (origin === 'button') return openAndFocus();
      }
      setActiveIndex(i => Math.min(i + 1, options.length - 1));
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        if (origin === 'button') return openAndFocus();
      }
      setActiveIndex(i => Math.max(i - 1, 0));
      return true;
    }
    return false;
  };

  return (
    <Fragment>
      <div css={filterMenuStyles.filterHead}>
        <h5 css={filterMenuStyles.filterTitle}>Distance</h5>
      </div>
      <div css={filterMenuStyles.filterBody}>
        {/* Custom dropdown with per-option icons */}
        <div ref={wrapperRef} css={styles.wrapper}>
          <button
            type="button"
            css={styles.select}
            className={isKeyboard ? 'keyboard-focus' : ''}
            onClick={() => (open ? setOpen(false) : openAndFocus())}
            onKeyDown={e => {
              if (handleKeyNavigation(e, { origin: 'button' })) return;
              if (e.key === 'Enter') {
                e.preventDefault();
                if (open && activeIndex >= 0) {
                  handleDistanceChange(options[activeIndex]);
                } else {
                  // Open the list but DO NOT focus any item; only Tab should move focus.
                  openButDontFocus();
                }
              }
            }}
            aria-expanded={open}
            aria-label="Distance"
            tabIndex={filterShowHide ? 0 : -1}
          >
            <span css={styles.valueText}>
              {selectedDistanceValue === 200 ? (
                <>
                  <span css={styles.valueNumber}>Nationwide</span>
                </>
              ) : (
                <>
                  Within{' '}
                  {userSelected ? (
                    <span css={styles.valueNumber}>
                      {selectedDistanceValue}
                    </span>
                  ) : (
                    <span>{selectedDistanceValue}</span>
                  )}{' '}
                  miles
                </>
              )}
            </span>
            <span css={styles.selectCaret} aria-hidden>
              <PlainDownArrow />
            </span>
          </button>

          {open && (
            <ul role="listbox" css={styles.dropdownDistanceList}>
              {options.map((opt, idx) => {
                const isActive = idx === activeIndex;
                return (
                  <li
                    id={`distance-option-${idx}`}
                    key={opt}
                    role="option"
                    aria-selected={selectedDistanceValue === opt}
                    tabIndex={-1}
                    css={styles.distanceOptionValues(isActive)}
                    className={isActive ? 'active' : ''}
                    onClick={() => handleDistanceChange(opt)}
                  >
                    <div css={styles.iconWrapper} aria-hidden>
                      <LocationSearchDefaultIcon />
                    </div>
                    <div
                      id={`distance-option-value-${idx}`}
                      css={styles.distanceListValue}
                      tabIndex={0}
                      ref={el => (listItemRefs.current[idx] = el)}
                      onKeyDown={e => {
                        if (handleKeyNavigation(e, { origin: 'item' })) return;
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleDistanceChange(opt);
                        }
                      }}
                    >
                      {opt === 200 ? 'Nationwide' : `${opt} miles`}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Fragment>
  );
};
export default FilterByDistance;

FilterByDistance.propTypes = {
  currentValue: PropTypes.number,
  onSave: PropTypes.func,
  disabilityOption: PropTypes.number,
};
